"use client";

import React from "react";
import { useEffect, useRef, useState } from "react";
import AppLogo from "@/components/ui/AppLogo";

import Icon from "@/components/ui/AppIcon";
import ThemeSwitcher from "@/components/ThemeSwitcher";

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const elements = heroRef.current?.querySelectorAll(".hero-animate");
    elements?.forEach((el, i) => {
      (el as HTMLElement).style.animationDelay = `${i * 0.15}s`;
      (el as HTMLElement).style.opacity = "0";
      setTimeout(() => {
        (el as HTMLElement).style.animation = `fadeSlideUp 1s cubic-bezier(0.16, 1, 0.3, 1) ${i * 0.15}s forwards`;
      }, 100);
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMobileMenuClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setIsMobileMenuOpen(false);
  };

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen"
      aria-labelledby="hero-heading">
      
      {/* Animated Background with Parallax */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-royal-purple via-deep-purple to-mid-purple dark:from-navy-900 dark:via-navy-800 dark:to-deep-purple overflow-hidden"
        style={{
          transform: `translateY(${scrollY * 0.5}px)`,
          transition: 'transform 0.1s ease-out'
        }}
      >
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute top-20 left-10 w-72 h-72 bg-gold dark:bg-amber-500 rounded-full mix-blend-multiply filter blur-3xl animate-float-a"
            style={{
              transform: `translate(${scrollY * 0.3}px, ${scrollY * 0.2}px)`,
              transition: 'transform 0.1s ease-out'
            }}
          />
          <div 
            className="absolute top-40 right-20 w-96 h-96 bg-pink-brand dark:bg-amber-600 rounded-full mix-blend-multiply filter blur-3xl animate-float-b"
            style={{
              transform: `translate(${-scrollY * 0.2}px, ${scrollY * 0.25}px)`,
              transition: 'transform 0.1s ease-out'
            }}
          />
          <div 
            className="absolute -bottom-20 left-1/3 w-80 h-80 bg-light-purple dark:bg-navy-600 rounded-full mix-blend-multiply filter blur-3xl animate-float-c"
            style={{
              transform: `translateY(${scrollY * 0.15}px)`,
              transition: 'transform 0.1s ease-out'
            }}
          />
        </div>
      </div>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 shadow-lg bg-navy-800`}
        aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between relative z-10">
          <AppLogo size={56} text="The Gyn Consult" />
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8 text-sm font-medium">
            <a
              href="#hero"
              className="text-white/90 hover:text-amber-400 transition-colors">
              Home
            </a>
            <a
              href="#services"
              className="text-white/90 hover:text-amber-400 transition-colors">
              Services
            </a>
            <a
              href="#specialist"
              className="text-white/90 hover:text-amber-400 transition-colors">
              PCOS/Endometriosis
            </a>
            <a
              href="#testimonials"
              className="text-white/90 hover:text-amber-400 transition-colors">
              Testimonials
            </a>
            <ThemeSwitcher />
            <a
              href="#booking"
              className="btn-gold px-6 py-2.5 text-sm inline-flex items-center gap-2">
              <Icon name="CalendarDaysIcon" size={16} variant="solid" />
              Book Now
            </a>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-3">
            <ThemeSwitcher />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-white hover:text-amber-400 hover:bg-navy-900 rounded-lg transition-colors"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}>
              {isMobileMenuOpen ?
              <Icon name="XMarkIcon" size={24} variant="outline" /> :

              <Icon name="Bars3Icon" size={24} variant="outline" />
              }
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen &&
        <div className="md:hidden bg-navy-900 border-t border-navy-800 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              <a
              href="#hero"
              onClick={handleMobileMenuClick}
              className="block px-4 py-3 text-sm font-medium text-white/90 hover:bg-navy-800 hover:text-amber-400 rounded-lg transition-colors">
                Home
              </a>
              <a
              href="#services"
              onClick={handleMobileMenuClick}
              className="block px-4 py-3 text-sm font-medium text-white/90 hover:bg-navy-800 hover:text-amber-400 rounded-lg transition-colors">
                Services
              </a>
              <a
              href="#specialist"
              onClick={handleMobileMenuClick}
              className="block px-4 py-3 text-sm font-medium text-white/90 hover:bg-navy-800 hover:text-amber-400 rounded-lg transition-colors">
                PCOS/Endometriosis Clinic
              </a>
              <a
              href="#testimonials"
              onClick={handleMobileMenuClick}
              className="block px-4 py-3 text-sm font-medium text-white/90 hover:bg-navy-800 hover:text-amber-400 rounded-lg transition-colors">
                Testimonials
              </a>
              <a
              href="#booking"
              onClick={handleMobileMenuClick}
              className="block px-4 py-3 text-sm font-medium text-white bg-gold hover:bg-gold/90 rounded-lg transition-colors text-center">
                Book Appointment
              </a>
            </div>
          </div>
        }
      </nav>

      {/* Hero Content */}
      <div className="relative pt-24 sm:pt-28 md:pt-32 pb-16 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-6 sm:space-y-8 text-white">
              <div className="hero-animate inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/20 border border-gold/40 text-gold text-xs font-bold uppercase tracking-wider">
                <Icon name="StarIcon" size={14} variant="solid" />
The Gyn Consult
              </div>

              <h1
                id="hero-heading"
                className="hero-animate font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight sm:leading-[0.95]">
                Northern Uganda's
                <span className="block text-gold mt-3 sm:mt-2">Premier</span>
                <span className="block text-pink-light mt-3 sm:mt-2">Women's Health Clinic</span>
              </h1>

              <p className="hero-animate text-base sm:text-base lg:text-lg text-white/80 leading-relaxed max-w-xl">
                Expert gynaecological & obstetric consultancy in Gulu. From fertility and PCOS to
                antenatal care and ultrasonography — all under one roof.
              </p>

              <div className="hero-animate flex flex-col sm:flex-row gap-4 sm:gap-4">
                <a
                  href="#booking"
                  className="btn-gold px-8 py-4 text-base inline-flex items-center justify-center gap-2 group min-h-[44px] touch-manipulation">
                  <Icon
                    name="CalendarDaysIcon"
                    size={20}
                    variant="solid"
                    className="transition-transform group-hover:scale-110" />
                  
                  Book Consultation
                </a>
                <a
                  href="https://wa.me/256775211731"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base rounded-full border-2 border-white/30 text-white font-semibold hover:bg-white hover:text-deep-purple transition-all group min-h-[44px] touch-manipulation">
                  <svg
                    className="w-5 h-5 transition-transform group-hover:rotate-12"
                    fill="currentColor"
                    viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  WhatsApp Us
                </a>
              </div>

              <div className="hero-animate flex flex-wrap gap-6 sm:gap-6 pt-6 sm:pt-4">
                {[
                { icon: "MapPinIcon", text: "Moroto Rd, Gulu" },
                { icon: "ClockIcon", text: "Mon–Sat: 8am–6pm" },
                { icon: "ShieldCheckIcon", text: "Certified Specialists" }].
                map((badge) =>
                <div key={badge.text} className="flex items-center gap-2 text-sm sm:text-sm text-white/80">
                    <Icon
                    name={badge.icon as "MapPinIcon"}
                    size={16}
                    variant="solid"
                    className="text-gold" />
                  
                    <span>{badge.text}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right Image Section */}
            <div className="hero-animate relative hidden lg:block">
              <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/assets/images/PNG_DOCTOR-1771710857516.png"
                  alt="Dr. Baguma Steven - Professional gynecologist and obstetrician at The Gyn Consult"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}